import { createContext, useContext, type ReactNode } from 'react'
import { GROUP_TYPES, type GroupType } from '../../types.ts'

/**
 * Capability-Flags fuer die Matrix-UIs. `INTERNAL_CAPS` ist der Default und
 * bildet den bisherigen SysMan-Admin-Funktionsumfang ab. `CUSTOMER_CAPS`
 * schraenkt die UI auf das ein, was der Kunde im Kundenportal bearbeiten darf;
 * der Backend-Guard (`structurePolicy.validateCustomerStructurePatch`) setzt
 * die Policy verbindlich durch, die Caps sind reine UI-Komfort-Schalter.
 */
export interface MatrixCapabilities {
  /** Server/Share/Drive anlegen oder loeschen */
  canEditInfrastructure: boolean
  /** Inline-Umbenennung von Laufwerk-Buchstaben, -Labels, Server-/Share-Namen */
  canEditDriveMetadata: boolean
  /** Excel-Import/-Export erlaubt */
  canImportExport: boolean
  /** Welche Gruppentypen der Nutzer neu anlegen darf */
  canCreateGroupTypes: ReadonlyArray<GroupType>
  variant: 'internal' | 'customer'
}

export const INTERNAL_CAPS: MatrixCapabilities = {
  canEditInfrastructure: true,
  canEditDriveMetadata: true,
  canImportExport: true,
  canCreateGroupTypes: GROUP_TYPES,
  variant: 'internal',
}

export const CUSTOMER_CAPS: MatrixCapabilities = {
  canEditInfrastructure: false,
  canEditDriveMetadata: false,
  canImportExport: true,
  canCreateGroupTypes: ['AB'],
  variant: 'customer',
}

const MatrixCapsCtx = createContext<MatrixCapabilities>(INTERNAL_CAPS)

export function MatrixCapabilitiesProvider({
  value,
  children,
}: {
  value: MatrixCapabilities
  children: ReactNode
}) {
  return <MatrixCapsCtx.Provider value={value}>{children}</MatrixCapsCtx.Provider>
}

export function useMatrixCaps(): MatrixCapabilities {
  return useContext(MatrixCapsCtx)
}
